// All NOVA API calls — mirrors the fetch/SSE pattern used elsewhere in the app.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_STORAGE_KEY = 'nova_session_key';

// Returns a stable per-tab session key, generating + persisting one on first use.
export function generateSessionKey() {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    // sessionStorage may be unavailable (private mode) — fall through to a fresh key
  }

  const key =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2);

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, key);
  } catch {
    /* ignore */
  }
  return key;
}

// Streams NOVA's reply, parsing the SSE protocol emitted by /nova/chat.
//   [NOVA_TOOL:name]   -> onToolUse(name)
//   [NOVA_RESULT:json] -> onToolResult(parsed)
//   [DONE]             -> onDone()
//   [ERROR:msg]        -> onError(msg)
//   anything else      -> onToken(text)
export async function streamNova({
  messages,
  sessionKey,
  onToken,
  onToolUse,
  onToolResult,
  onDone,
  onError,
}) {
  try {
    const response = await fetch(`${BACKEND_URL}/nova/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, session_key: sessionKey }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;

    while (true) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // keep the last (possibly partial) line in the buffer
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);

        if (data === '[DONE]') {
          done = true;
          onDone && onDone();
          continue;
        }
        if (data.startsWith('[ERROR:') && data.endsWith(']')) {
          onError && onError(data.slice(7, -1));
          continue;
        }
        if (data.startsWith('[NOVA_TOOL:') && data.endsWith(']')) {
          onToolUse && onToolUse(data.slice(11, -1));
          continue;
        }
        if (data.startsWith('[NOVA_RESULT:') && data.endsWith(']')) {
          const raw = data.slice(13, -1);
          try {
            onToolResult && onToolResult(JSON.parse(raw));
          } catch {
            onToolResult && onToolResult(null);
          }
          continue;
        }
        // plain text token
        onToken && onToken(data);
      }
    }

    if (!done) onDone && onDone();
  } catch (e) {
    onError && onError(e?.message || String(e));
  }
}
